import { create } from "zustand";
import { resolveWidgetLocale } from "@/lib/widget-locale";

type Studio = {
  id: string;
  slug: string;
  name: string;
  timezone: string;
  currency: string;
  defaultLocale: string;
  /** Couleur du studio, celle de son site BookZen. data-primary la remplace. */
  primaryColor: string | null;
  /** Langue choisie dans l'admin ("auto" = celle du visiteur). */
  widgetLocale?: string | null;
};

export type WidgetState = {
  studioId: string | null;
  studio: Studio | null;
  status: "idle" | "loading" | "ready" | "error";
  error?: string;

  setStudioId: (id: string) => void;
  loadStudio: (id?: string) => Promise<void>;
};

export const useStudioStore = create<WidgetState>((set, get) => ({
  studioId: null,
  studio: null,
  status: "idle",

  setStudioId: (id) =>
    set({
      studioId: id,
      status: "loading",
      error: undefined,
    }),

  loadStudio: async (id?: string) => {
    const studioId = id ?? get().studioId;
    if (!studioId) return;

    set({ status: "loading" });

    try {
      const res = await takePrefetchedStudio(studioId) ?? (await fetch(`/api/widget/public/studios/${studioId}`));

      if (!res.ok) {
        throw new Error("Studio not found");
      }

      const data = await res.json();
      // Avant `ready` : les premiers appels d'API partent deja dans la bonne langue.
      resolveWidgetLocale(data.studio?.widgetLocale, data.studio?.defaultLocale);

      set({
        studio: data.studio,
        status: "ready",
      });
    } catch {
      set({
        status: "error",
        error: "Invalid or inactive studio",
      });
    }
  },
}));

/**
 * La requete lancee par index.html avant le bundle, une seule fois et pour ce
 * studio-la ; sinon (rechargement de la fiche, autre studio) un fetch normal.
 */
async function takePrefetchedStudio(studioId: string): Promise<Response | null> {
  const prefetched = (window as { __bzStudio?: { id: string; request: Promise<Response> } }).__bzStudio;
  if (!prefetched || prefetched.id !== studioId) return null;
  delete (window as { __bzStudio?: unknown }).__bzStudio;
  return prefetched.request.catch(() => null);
}
